import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { environment } from '../../../../environments/environment';

/** Minimum allowed declared value in dollars (inclusive). */
export const DECLARED_VALUE_MIN = 1;

/**
 * Declared values greater than this amount are not supported online and block shipment creation.
 * Sourced from `environment.declaredValueMaxExclusive`.
 */
export const DECLARED_VALUE_MAX_EXCLUSIVE = environment.declaredValueMaxExclusive;

/** Up to 2 decimal places; optional leading digits before the decimal. */
const DECLARED_VALUE_NUMERIC_PATTERN = /^\d+(\.\d{1,2})?$/;

/** Error keys used by declared-value validators. */
export const DECLARED_VALUE_VALIDATION_ERROR_KEYS = {
  invalid: 'declaredValueInvalid',
  aboveMax: 'declaredValueAboveMax',
  descriptionRequired: 'packageDescriptionRequired',
} as const;

/**
 * Parses a declared-value control value into a finite number, or null when blank.
 */
export function parseDeclaredValue(raw: unknown): number | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  const text = typeof raw === 'string' ? raw.trim() : String(raw).trim();
  if (!text) {
    return null;
  }

  if (!DECLARED_VALUE_NUMERIC_PATTERN.test(text)) {
    return Number.NaN;
  }

  return Number(text);
}

/**
 * Returns whether package description must be provided for the given declared value.
 */
export function isDeclaredValueRequiredDescription(raw: unknown): boolean {
  const parsed = parseDeclaredValue(raw);
  return parsed !== null && !Number.isNaN(parsed) && parsed > 0;
}

/**
 * Validates an optional declared-value field.
 * Blank values are allowed. Values outside $1.00–$5000.00 produce Invalid;
 * values greater than $5000.00 produce a blocking above-max error.
 */
export function optionalDeclaredValueValidator(control: AbstractControl): ValidationErrors | null {
  const parsed = parseDeclaredValue(control.value);

  if (parsed === null) {
    return null;
  }

  if (Number.isNaN(parsed) || parsed < DECLARED_VALUE_MIN) {
    return { [DECLARED_VALUE_VALIDATION_ERROR_KEYS.invalid]: true };
  }

  if (parsed > DECLARED_VALUE_MAX_EXCLUSIVE) {
    return { [DECLARED_VALUE_VALIDATION_ERROR_KEYS.aboveMax]: true };
  }

  return null;
}

/**
 * Requires a non-whitespace package description when declared value is greater than zero.
 *
 * @param declaredValueControl - Control holding the shipment declared value
 */
export function requiredPackageDescriptionWhenDeclaredValueValidator(
  declaredValueControl: AbstractControl,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!isDeclaredValueRequiredDescription(declaredValueControl.value)) {
      return null;
    }

    const description = typeof control.value === 'string' ? control.value.trim() : '';
    if (description) {
      return null;
    }

    return { [DECLARED_VALUE_VALIDATION_ERROR_KEYS.descriptionRequired]: true };
  };
}
